/**
 * SkuRecommendationsModal — the "SKU click magic moment" side panel.
 *
 * Opens when a brand clicks a SKU tile on the Build Wave page. Pulls
 * historical / vertical / curated-demo recommendations and surfaces
 * one-click "Apply" actions that hot-wire the existing wave-builder
 * form (caption style, top creators, optimal angle).
 *
 * Tier-aware:
 *   - starter   → auto-apply all recommendations on open
 *   - pro / ent → opens with manual review, user clicks Apply per item
 *
 * Backward compatible: when there's no history, falls back to a
 * "still learning" empty state with vertical-level defaults.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X, Sparkles, Users, Wand2, Hash, Clock, ChevronRight,
  Check, Loader2, Lock, TrendingUp, Megaphone,
} from 'lucide-react';
import { T, P, BG, CARD, BORDER } from './constants';
import ProBadge from '../ui/ProBadge';
import FirstPartyDataPanel from '../dna/FirstPartyDataPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const ANGLE_LABEL = {
  pov: 'POV',
  POV: 'POV',
  tutorial: 'Tutorial',
  reaction: 'Reaction',
  confession: 'Confession',
  day_in_life: 'Day-in-the-life',
  day_in_the_life: 'Day-in-the-life',
};

export default function SkuRecommendationsModal({
  sku,
  open,
  onClose,
  onApply,         // (kind, payload) — kind ∈ "creators" | "caption" | "angle" | "timing" | "all"
  userTier,        // "starter" | "pro" | "enterprise"
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appliedKinds, setAppliedKinds] = useState(new Set());
  const isStarter = (userTier || 'starter').toLowerCase() === 'starter';

  // Fetch + auto-apply for Starter tier on first open
  useEffect(() => {
    if (!open || !sku?.id) return;
    setAppliedKinds(new Set());
    setError('');
    setData(null);
    setLoading(true);

    let alive = true;
    axios
      .get(`${API}/api/brand/skus/${encodeURIComponent(sku.id)}/recommendations`, { withCredentials: true })
      .then(({ data: payload }) => {
        if (!alive) return;
        setData(payload);
        // Starter tier: auto-apply all on open — the "magic moment" itself.
        // Pro / Enterprise: leave to the user to review and pick.
        if (payload && (payload.tier || 'starter').toLowerCase() === 'starter') {
          handleApplyAll(payload, /* announce */ false);
        }
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || 'Could not load recommendations');
      })
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sku?.id]);

  if (!open) return null;

  const handleApplyAll = (payload = data, announce = true) => {
    if (!payload) return;
    onApply?.('all', payload);
    setAppliedKinds(new Set(['creators', 'caption', 'angle', 'timing']));
    if (announce) onClose?.();
  };

  const handleApply = (kind, value) => {
    onApply?.(kind, value);
    setAppliedKinds((prev) => new Set([...prev, kind]));
  };

  return (
    <div
      data-testid="sku-recommendations-modal"
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{ background: 'rgba(0, 0, 0, 0.65)' }}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <aside
        className="w-full max-w-[480px] h-full overflow-y-auto"
        style={{ background: '#0A0A0A', borderLeft: `1px solid ${BORDER}` }}
      >
        <Header sku={sku} onClose={onClose} userTier={userTier} />

        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {data && (
          <div className="px-5 pb-8 space-y-5">
            {/* A3(b) — demo-data provenance label. Every demo-sourced
                number must render with a visible "illustrative / demo
                data" mark (platform-review compliance). */}
            {data.data_provenance === 'demo_curated' && (
              <div
                data-testid="demo-data-banner"
                className="rounded-lg px-3 py-2 flex items-start gap-2"
                style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.45)' }}
              >
                <Sparkles className="h-3.5 w-3.5 mt-0.5 text-amber-400" />
                <p className="text-[11px] leading-relaxed text-amber-400">
                  <span className="font-black uppercase tracking-wider">Illustrative · demo data</span>
                  <span className="text-gray-400 ml-1">
                    Numbers below are hand-curated for demonstration — not live engine output.
                  </span>
                </p>
              </div>
            )}
            {/* Starter auto-applied banner */}
            {isStarter && appliedKinds.size > 0 && (
              <div
                data-testid="auto-applied-banner"
                className="rounded-lg px-3 py-2 flex items-start gap-2"
                style={{ background: `${T}10`, border: `1px solid ${T}55` }}
              >
                <Sparkles className="h-3.5 w-3.5 mt-0.5" style={{ color: T }} />
                <p className="text-[11px] leading-relaxed" style={{ color: T }}>
                  <span className="font-black uppercase tracking-wider">Auto-applied · Starter</span>
                  <span className="text-gray-400 ml-1">
                    We pre-filled cohort, captions, and angle. Review your wave below or hit "Send wave" to launch.
                  </span>
                </p>
              </div>
            )}

            {/* Rationale */}
            {(data.rationale || []).length > 0 && (
              <section data-testid="reco-rationale" className="space-y-1.5">
                {data.rationale.map((line, i) => (
                  <p key={`rationale-${i}-${(line || '').slice(0, 24)}`} className="text-[11px] leading-relaxed text-gray-400 flex items-start gap-1.5">
                    <Sparkles className="h-2.5 w-2.5 mt-1 shrink-0" style={{ color: T }} />
                    {line}
                  </p>
                ))}
              </section>
            )}

            {/* 1st-party data signals (safe surface) — explains which
                data categories of the brand's own data shaped these
                recs. No projected lift / per-creator claims. */}
            <FirstPartyDataPanel
              signals={data.first_party_signals}
              testId="reco-first-party-panel"
            />

            {/* Top creators */}
            <Card title="Top creators for this SKU" icon={Users} kind="creators" appliedKinds={appliedKinds}>
              {(data.creators || []).length === 0 ? (
                <EmptyRow text="No creator history yet — your first wave seeds this list." />
              ) : (
                <div className="space-y-1.5">
                  {data.creators.slice(0, isStarter ? 3 : 8).map((c) => (
                    <CreatorRow key={c.handle} c={c} illustrative={!!data.illustrative} />
                  ))}
                  {!isStarter && data.creators.length > 3 && (
                    <p className="text-[10px] text-gray-600 px-2">
                      Showing {Math.min(8, data.creators.length)} of {data.creators.length} historical creators
                    </p>
                  )}
                  <ApplyBtn
                    testId="apply-creators"
                    label="Apply these creators"
                    onClick={() => handleApply('creators', data.creators)}
                    applied={appliedKinds.has('creators')}
                  />
                </div>
              )}
            </Card>

            {/* Winning angles */}
            <Card title="Best-performing angles" icon={Wand2} kind="angle" appliedKinds={appliedKinds}>
              <div className="space-y-1.5">
                {(data.angles || []).slice(0, 3).map((a) => (
                  <AngleRow key={a.angle} a={a} />
                ))}
                <ApplyBtn
                  testId="apply-angle"
                  label={`Copy top angle (${ANGLE_LABEL[data.angles?.[0]?.angle] || '—'})`}
                  onClick={() => handleApply('angle', data.angles?.[0])}
                  applied={appliedKinds.has('angle')}
                  disabled={!data.angles?.[0]}
                />
              </div>
            </Card>

            {/* Captions */}
            <Card title="Winning captions" icon={Hash} kind="caption" appliedKinds={appliedKinds}>
              <div className="space-y-2">
                {(data.captions || []).slice(0, 3).map((cap, i) => (
                  <CaptionRow key={`caption-${i}-${(cap?.text || '').slice(0, 24)}`} cap={cap} onUse={() => handleApply('caption', cap)} />
                ))}
              </div>
            </Card>

            {/* Timing */}
            <Card title="Optimal timing + platform mix" icon={Clock} kind="timing" appliedKinds={appliedKinds}>
              <TimingRow timing={data.timing} />
              <ApplyBtn
                testId="apply-timing"
                label="Use these timing defaults"
                onClick={() => handleApply('timing', data.timing)}
                applied={appliedKinds.has('timing')}
              />
            </Card>

            {/* Tier upsell */}
            {isStarter && (
              <UpgradeCard />
            )}

            {/* Footer action */}
            <div className="sticky bottom-0 pt-3 pb-1" style={{ background: '#0A0A0A' }}>
              <button
                onClick={() => handleApplyAll(data, true)}
                data-testid="apply-all-learnings"
                className="w-full py-3 rounded-lg font-black text-xs uppercase tracking-wider transition"
                style={{
                  background: `linear-gradient(90deg, ${T}, ${P})`,
                  color: '#0A0A0A',
                  boxShadow: `0 0 24px ${T}30`,
                }}
              >
                <Sparkles className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                Apply all learnings & close
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}


// ─── Sub-components ──────────────────────────────────────────────────

function Header({ sku, onClose, userTier }) {
  return (
    <div
      className="sticky top-0 z-10 px-5 py-4 flex items-center gap-3 border-b"
      style={{ background: '#0A0A0A', borderColor: BORDER }}
    >
      {sku?.image ? (
        <img src={sku.image} alt={sku?.name} className="w-12 h-12 rounded-lg object-cover" />
      ) : (
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: BG }}>
          <Megaphone className="h-5 w-5 text-gray-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-[0.22em] font-black" style={{ color: T }}>
          SKU Intelligence
        </p>
        <h3 className="text-base font-bold text-white truncate" data-testid="reco-sku-name">{sku?.name || 'SKU'}</h3>
        {sku?.category && <p className="text-[10px] text-gray-500 uppercase tracking-wider">{sku.category}</p>}
      </div>
      <span
        className="text-[9px] uppercase tracking-wider font-black px-2 py-1 rounded"
        style={{ background: `${T}14`, color: T, border: `1px solid ${T}55` }}
        data-testid="reco-tier-badge"
      >
        {(userTier || 'starter').toUpperCase()}
      </span>
      <button onClick={onClose} className="p-1 rounded hover:bg-white/5 transition" data-testid="reco-modal-close">
        <X className="h-4 w-4 text-gray-400" />
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="px-5 py-10 text-center text-[12px] text-gray-500 flex items-center justify-center gap-2">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      Pulling recommendations…
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="px-5 py-6">
      <p className="text-[12px]" style={{ color: P }}>{message}</p>
    </div>
  );
}

function Card({ title, icon: Icon, kind, appliedKinds, children }) {
  const applied = appliedKinds.has(kind);
  return (
    <section
      className="rounded-lg p-4"
      style={{ background: CARD, border: `1px solid ${applied ? T + '55' : BORDER}` }}
      data-testid={`reco-card-${kind}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-3.5 w-3.5" style={{ color: T }} />
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-white">{title}</h4>
        {applied && <Check className="h-3 w-3 ml-auto" style={{ color: T }} />}
      </div>
      {children}
    </section>
  );
}

function CreatorRow({ c, illustrative = false }) {
  // A3(c): live (non-demo) rows carry no heuristic lift values — the
  // backend strips them; we render the engine match score. Demo rows
  // keep curated numbers but wear the demo chip.
  const score = c.lift_score ?? c.match_score;
  return (
    <div className="flex items-center justify-between px-2.5 py-1.5 rounded text-[11px]"
         style={{ background: BG }}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-bold text-white truncate">{c.handle}</span>
        <span className="text-gray-500 text-[10px] truncate">{c.name}</span>
        {illustrative && (
          <span className="text-[8px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider text-amber-400"
                style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)' }}
                data-testid="demo-creator-chip">demo</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-500 uppercase">{c.platform}</span>
        <span className="font-black" style={{ color: T }}>{Math.round(score || 0)}</span>
      </div>
    </div>
  );
}

function AngleRow({ a }) {
  const pct = Math.round((a.win_rate || 0) * 100);
  return (
    <div className="flex items-center justify-between px-2.5 py-1.5 rounded text-[11px]"
         style={{ background: BG }}>
      <span className="font-bold text-white">{ANGLE_LABEL[a.angle] || a.angle}</span>
      <span className="text-gray-400 text-[10px]">
        win {pct}% · eng {((a.avg_engagement || 0) * 100).toFixed(1)}%
      </span>
    </div>
  );
}

function CaptionRow({ cap, onUse }) {
  return (
    <div className="rounded p-2.5 space-y-2" style={{ background: BG }}>
      <p className="text-[12px] text-white leading-snug">{cap.text}</p>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] text-gray-500 uppercase tracking-wider">
          {ANGLE_LABEL[cap.angle] || cap.angle} · {cap.platform}
        </span>
        <button
          onClick={onUse}
          data-testid={`use-caption-${cap.angle}`}
          className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded transition hover:brightness-110"
          style={{ background: `${T}1A`, color: T, border: `1px solid ${T}55` }}
        >
          Use this style
          <ChevronRight className="h-2.5 w-2.5 inline-block ml-0.5" />
        </button>
      </div>
    </div>
  );
}

function TimingRow({ timing }) {
  if (!timing) return null;
  return (
    <div className="space-y-1.5 mb-2.5 text-[11px]">
      <Stat label="Best hour" value={`${timing.best_hour_local || 19}:00 local`} />
      <Stat label="Best day" value={timing.best_day || 'Thursday'} />
      <div className="flex items-center justify-between px-2.5 py-1.5 rounded" style={{ background: BG }}>
        <span className="text-gray-400 text-[10px] uppercase tracking-wider">Platform mix</span>
        <span className="text-white font-mono text-[10px]">
          {Object.entries(timing.platform_mix || {}).map(([p, v]) =>
            `${p[0].toUpperCase()}:${Math.round(v * 100)}%`).join(' · ')}
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between px-2.5 py-1.5 rounded" style={{ background: BG }}>
      <span className="text-gray-400 text-[10px] uppercase tracking-wider">{label}</span>
      <span className="text-white font-bold">{value}</span>
    </div>
  );
}

function ApplyBtn({ label, onClick, applied, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={applied || disabled}
      data-testid={testId}
      className="w-full mt-2 px-3 py-2 rounded text-[11px] font-black uppercase tracking-wider transition disabled:opacity-50"
      style={{
        background: applied ? `${T}30` : BG,
        color: applied ? T : '#FFFFFF',
        border: `1px solid ${applied ? T : BORDER}`,
      }}
    >
      {applied
        ? <><Check className="h-3 w-3 inline-block mr-1 -mt-0.5" />Applied</>
        : <>{label}<ChevronRight className="h-3 w-3 inline-block ml-1 -mt-0.5" /></>
      }
    </button>
  );
}

function EmptyRow({ text }) {
  return (
    <p className="text-[11px] text-gray-500 italic px-1">{text}</p>
  );
}

function UpgradeCard() {
  return (
    <div
      className="rounded-lg p-4 space-y-2"
      style={{
        background: `linear-gradient(135deg, ${P}11, ${T}11)`,
        border: `1px dashed ${P}55`,
      }}
      data-testid="upgrade-card"
    >
      <div className="flex items-center gap-2">
        <ProBadge tier="pro" />
        <ProBadge tier="enterprise" />
      </div>
      <p className="text-[11px] text-gray-300 leading-relaxed">
        <ProBadge tier="pro" size="sm" className="mr-1" /> unlocks 12-month SKU history, A/B caption variants and manual cohort review.{' '}
        <ProBadge tier="enterprise" size="sm" className="mx-1" /> adds white-glove KPI calibration and bespoke creative review.
      </p>
      <button
        className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded"
        style={{ background: P, color: '#0A0A0A' }}
        data-testid="upgrade-btn"
        onClick={() => window.open('mailto:founders@wavedsp.ai?subject=Upgrade%20to%20Pro', '_blank')}
      >
        <TrendingUp className="h-2.5 w-2.5 inline-block mr-1 -mt-0.5" />
        Talk to us about upgrading
      </button>
    </div>
  );
}
